import React, {
  ComponentProps,
  FC,
  MouseEvent,
  useMemo,
  useCallback,
} from 'react';

import { Box } from '@adsk/alloy-react-box';
import { MenuItem, MenuList } from '@adsk/alloy-react-menu';
import theme, { StylableComponent } from '@adsk/alloy-react-theme';

import { Product } from '../../../models';
import { productsParserWithSelectedItem } from '../../../utils';

type ProductsListProps = StylableComponent<HTMLDivElement> & {
  products?: Product[];
  selectedProductId?: string;
  onProductClick?: (
    event: MouseEvent,
    { url, id, name }: { url: string; id: string; name: string },
  ) => void;
};

export const ProductsList: FC<ProductsListProps> = ({
  products,
  selectedProductId,
  onProductClick,
}) => {
  const productsWithHighlight = useMemo(
    () => productsParserWithSelectedItem(products, selectedProductId),
    [products, selectedProductId],
  );

  const getMenuItemProps = useCallback(
    ({
      id,
      name,
      url,
    }: {
      id: string;
      name: string;
      url: string;
    }): ComponentProps<typeof MenuItem> => ({
      as: 'button',
      'data-testid': `product-entry-${id}`,
      nested: false,
      showSelection: false,
      onMouseDown: (event) => {
        onProductClick?.(event, { url, id, name });
      },
    }),
    [onProductClick],
  );

  return (
    <MenuList>
      {productsWithHighlight.map(
        ({ id, name, url, iconUrl, highlight, hasDivider }) => (
          <MenuItem
            {...getMenuItemProps({ id, name, url })}
            key={id}
            highlighted={highlight}
            style={
              hasDivider
                ? {
                    marginTop: 8,
                    paddingTop: 20,
                    paddingBottom: 20,
                    borderTop: `1px solid ${theme.colors.charcoal200}`,
                  }
                : undefined
            }
          >
            {iconUrl && (
              <span
                style={{
                  maskImage: `url(${iconUrl})`,
                  WebkitMaskImage: `url(${iconUrl})`,
                  backgroundColor: highlight
                    ? theme.colors.white
                    : theme.colors.charcoal900,
                  width: 24,
                  height: 24,
                }}
              />
            )}
            <Box
              typography="bodyMedium"
              ellipsis="single"
              flex={1}
              paddingLeft={10}
              textAlign="left"
            >
              {name}
            </Box>
          </MenuItem>
        ),
      )}
    </MenuList>
  );
};
